"use client"

import { useState } from "react"
import { Check } from "lucide-react"
import { cn } from "@/lib/utils"
import type { Language, LeadField, LeadFormValues } from "@/lib/chatbot/types"
import { useSpamGuard } from "@/components/spam/use-spam-guard"
import { BOTON, CAMPO } from "./estilos"

interface LeadFormProps {
  fields: LeadField[]
  language: Language
  disabled?: boolean
  onSubmit: (values: LeadFormValues, spam: Record<string, string>) => void
}

const COPY = {
  name: { es: "Nombre", en: "Name" },
  email: { es: "Email", en: "Email" },
  phone: { es: "Teléfono", en: "Phone" },
  rgpd: {
    es: "Acepto la política de privacidad y el tratamiento de mis datos.",
    en: "I accept the privacy policy and the processing of my data.",
  },
  submit: { es: "Enviar", en: "Send" },
  required: { es: "Campo obligatorio", en: "Required field" },
  invalidEmail: { es: "Email no válido", en: "Invalid email" },
  needRgpd: { es: "Debes aceptar la política de privacidad", en: "You must accept the privacy policy" },
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function LeadForm({ fields, language, disabled, onSubmit }: LeadFormProps) {
  const [values, setValues] = useState<LeadFormValues>({
    name: "",
    email: "",
    phone: "",
    rgpd: false,
  })
  const [errors, setErrors] = useState<Partial<Record<LeadField, string>>>({})
  const spam = useSpamGuard()

  const isComplete =
    (!fields.includes("name") || values.name.trim().length > 0) &&
    (!fields.includes("email") || EMAIL_RE.test(values.email.trim())) &&
    (!fields.includes("phone") || values.phone.trim().length > 0) &&
    (!fields.includes("rgpd") || values.rgpd)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const next: Partial<Record<LeadField, string>> = {}

    if (fields.includes("name") && !values.name.trim()) next.name = COPY.required[language]
    if (fields.includes("email")) {
      const e = values.email.trim()
      if (!e) next.email = COPY.required[language]
      else if (!EMAIL_RE.test(e)) next.email = COPY.invalidEmail[language]
    }
    if (fields.includes("phone") && !values.phone.trim()) next.phone = COPY.required[language]
    if (fields.includes("rgpd") && !values.rgpd) next.rgpd = COPY.needRgpd[language]

    if (Object.keys(next).length > 0) {
      setErrors(next)
      return
    }
    setErrors({})
    // Wait for the captcha token — submitting without it is rejected 403.
    if (!spam.ready) return
    onSubmit(
      {
        name: values.name.trim(),
        email: values.email.trim(),
        phone: values.phone.trim(),
        rgpd: values.rgpd,
      },
      spam.getSpamPayload()
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      {spam.fields}
      {fields.includes("name") && (
        <Field error={errors.name}>
          <input
            type="text"
            value={values.name}
            placeholder={COPY.name[language]}
            onChange={e => setValues(v => ({ ...v, name: e.target.value }))}
            disabled={disabled}
            className={CAMPO}
          />
        </Field>
      )}
      {fields.includes("email") && (
        <Field error={errors.email}>
          <input
            type="email"
            value={values.email}
            placeholder={COPY.email[language]}
            onChange={e => setValues(v => ({ ...v, email: e.target.value }))}
            disabled={disabled}
            className={CAMPO}
          />
        </Field>
      )}
      {fields.includes("phone") && (
        <Field error={errors.phone}>
          <input
            type="tel"
            value={values.phone}
            placeholder={COPY.phone[language]}
            onChange={e => setValues(v => ({ ...v, phone: e.target.value }))}
            disabled={disabled}
            className={CAMPO}
          />
        </Field>
      )}
      {fields.includes("rgpd") && (
        <Field error={errors.rgpd}>
          <label className="flex cursor-pointer items-start gap-2 text-xs text-gray-500">
            <input
              type="checkbox"
              checked={values.rgpd}
              onChange={e => setValues(v => ({ ...v, rgpd: e.target.checked }))}
              disabled={disabled}
              className="peer sr-only"
            />
            {/* La misma casilla que las opciones de los filtros. */}
            <span
              className={cn(
                "flex size-4 shrink-0 items-center justify-center rounded-[4px] border peer-focus-visible:ring-2 peer-focus-visible:ring-[#193144]/30",
                values.rgpd ? "border-[#193144] bg-[#193144] text-white" : "border-gray-300"
              )}
            >
              {values.rgpd && <Check className="size-3" aria-hidden="true" />}
            </span>
            <span>{COPY.rgpd[language]}</span>
          </label>
        </Field>
      )}
      <button
        type="submit"
        disabled={disabled || !spam.ready || !isComplete}
        className={BOTON}
      >
        {COPY.submit[language]}
      </button>
    </form>
  )
}

function Field({ error, children }: { error?: string; children: React.ReactNode }) {
  return (
    <div>
      {children}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  )
}
